import {hasOpenCrmForm} from '../../lib/onboarding/consultant/fluWorkspace';
import {useAuth, useUserData} from '../../lib/auth';
import {watchFluActivities,watchFluNotes,watchFluClient} from '../../lib/onboarding/consultant/fluActivityRefresh';
import { useDrawerEscape } from '../../lib/hooks/useDrawerEscape';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { useClientStore } from '../../lib/stores/clientStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useUserStore } from '../../lib/stores/userStore';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { useDealStore } from '../../lib/stores/dealStore';
import { useCalendarStore } from '../../lib/stores/calendarStore';
import { useCustomStatesStore } from '../../lib/stores/customStatesStore';
import { useLeadPipelineStore } from '../../lib/stores/leadPipelineStore';
import { useLeadSourceStore } from '../../lib/stores/leadSourceStore';
import { usePartnerStore } from '../../lib/stores/partnerStore';
import { useApp } from '../../components/AppProvider';
import { useAiHelpStore } from '../../lib/stores/aiHelpStore';
import { supabase } from '../../lib/supabase';
import { leadsRealtimeService } from '../../lib/services/leadsRealtimeService';
import { LeadDetailsHeader } from '../../components/leads/LeadDetailsHeader';
import { LeadMobileLayout } from '../../components/leads/LeadMobileLayout';
import { LeadDesktopLayout } from '../../components/leads/LeadDesktopLayout';
import { LeadNotesModal } from '../../components/leads/LeadNotesModal';
import { DealForm } from '../../components/deals/DealForm';
import { CreateClientDrawer } from '../../components/clients/create-drawer/CreateClientDrawer';
import { clientToDrawerInitialData } from '../../components/clients/create-drawer/formDefaults';
import LeadStatusUpdateDrawer from '../../components/leads/LeadStatusUpdateDrawer';
import { PartnerStatusUpdateDrawer } from '../../components/partners/PartnerStatusUpdateDrawer';
import type { ComposerDrawerPayload, ComposerMode } from '../../components/shared/timeline/InlineComposer';
import { toComposerDateInputValue, toComposerTimeInputValue } from '../../components/shared/timeline/composerDueDate';
import { PartnerSelector } from '../../components/partners/PartnerSelector';
import { PartnerForm } from '../../components/partners/PartnerForm';
import { NotionDrawer } from '../../components/ui/notion/NotionDrawer';
import { translations } from '../../lib/i18n/translations';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';
import type { LeadStage } from '../../lib/types';
import {
  HeaderSkeleton,
  CardSkeleton,
  StatusManagerSkeleton,
  ContactsSkeleton,
  InterestsSkeleton,
  TagsSkeleton,
  NotesSkeleton,
  CustomFieldsSkeleton
} from '../../components/ui/SkeletonLoader';
import { AlfaDocsService } from '../../lib/services/alfadocsService';
import { getAlfaDocsLinks, getPrimaryPracticeId } from '../../lib/services/alfadocsLinks';
import { usePartnerEntity } from '../partners/usePartnerEntity';

// === AlfaDocs multi-studio: write-back per studio in delete attività (Task 13) ===
// Cerca l'appuntamento AlfaDocs su TUTTI gli studi collegati al cliente (fallback quando manca
// l'alfadocs_appointment_id in metadata, es. attività storiche pre-multi-connessione).
async function findLinkedAlfaAppointment(
  clientId: string,
  activity: { due_date: string; title?: string },
  organizationId: string
): Promise<{ appointmentId: string; practiceId: string } | null> {
  const { data: clientData } = await supabase.from('clients').select('metadata').eq('id', clientId).single();
  const configs = (await AlfaDocsService.listConfigs(organizationId)).filter(c => c.is_active);
  if (configs.length === 0) return null;
  const oldestPracticeId = configs[0]?.configuration?.practice_id ?? null;
  const links = getAlfaDocsLinks(clientData?.metadata, oldestPracticeId);
  const practiceIds = Object.keys(links);
  if (practiceIds.length === 0) return null;
  const actDate = new Date(activity.due_date);
  const dateStr = actDate.toISOString().split('T')[0];
  for (const practiceId of practiceIds) {
    const cfg = configs.find(c => c.configuration?.practice_id === practiceId);
    const patientId = links[practiceId]?.patient_id;
    if (!cfg || !patientId) continue;
    try {
      const { data: appts } = await supabase.functions.invoke('alfadocs-proxy', {
        body: { action: 'appointments', params: { patientId, dateStart: dateStr, dateEnd: dateStr }, configId: cfg.id }
      });
      const appointments = appts?.data || [];
      const match = appointments.find((a: any) => {
        if (a.state === 'cancelled') return false;
        const aptTime = new Date(a.date);
        const sameHour = aptTime.getHours() === actDate.getHours() && aptTime.getMinutes() === actDate.getMinutes();
        return sameHour || a.description === activity.title;
      });
      if (match) return { appointmentId: String(match.id), practiceId };
    } catch (e) {
      console.warn('[findLinkedAlfaAppointment] lookup failed for practice', practiceId, e);
    }
  }
  return null;
}

// Risolve il configId AlfaDocs per il cancel (update state='cancelled'):
// practice dell'APPUNTAMENTO (metadata o practice del lookup) → fallback primaria cliente →
// fallback connessione attiva più vecchia.
async function resolveAlfaCancelConfigId(
  practiceIdHint: string | null | undefined,
  clientId: string,
  organizationId: string
): Promise<string | undefined> {
  const configs = (await AlfaDocsService.listConfigs(organizationId)).filter(c => c.is_active);
  if (configs.length === 0) return undefined;
  const oldestPracticeId = configs[0]?.configuration?.practice_id ?? null;
  let practiceId = practiceIdHint || undefined;
  if (!practiceId) {
    const { data: clientData } = await supabase.from('clients').select('metadata').eq('id', clientId).single();
    practiceId = getPrimaryPracticeId(clientData?.metadata, oldestPracticeId) || undefined;
  }
  if (!practiceId) practiceId = oldestPracticeId || undefined;
  const cfg = configs.find(c => c.configuration?.practice_id === practiceId) || configs[0];
  return cfg?.id;
}

// === Task 2 "Partner drawer unificato": query attività unica per la scheda ===
// Fattorizza gli 8 siti che ripetevano la stessa query (prima duplicata inline in
// fetchAllData, nel realtime, in handleEditTask/handleDeleteTask/handleDeleteActivity,
// in refreshActivities e nei callback onActivityAdded/onActivityUpdated del drawer).
// - lead/client: filtro IDENTICO a oggi, `.or(lead_id.eq.<id>,client_id.eq.<id>)`.
// - partner: `.eq('partner_id', id)`.
// `includeAssignee` preserva la select ESATTA di ciascun sito originale: alcuni
// includevano l'embed `assignee`, altri no (comportamento invariato, non unificato).
const ACTIVITIES_SELECT_WITH_ASSIGNEE = `
  *,
  users:user_profiles!sales_activities_user_id_fkey1(
    id,
    email,
    full_name,
    profile_image_url,
    avatar_url
  ),
  assignee:user_profiles!sales_activities_assigned_to_fkey(
    id,
    email,
    full_name,
    profile_image_url,
    avatar_url
  )
`;

const ACTIVITIES_SELECT_USERS_ONLY = `
  *,
  users:user_profiles!sales_activities_user_id_fkey1(
    id,
    email,
    full_name,
    profile_image_url,
    avatar_url
  )
`;

async function fetchEntityActivities(
  entityId: string | undefined,
  entityKind: 'lead' | 'client' | 'partner',
  includeAssignee: boolean = true,
  organizationId?: string
) {
  const base = supabase
    .from('sales_activities')
    .select(includeAssignee ? ACTIVITIES_SELECT_WITH_ASSIGNEE : ACTIVITIES_SELECT_USERS_ONLY);

  const scoped = organizationId ? base.eq('organization_id', organizationId) : base;
  const filtered = entityKind === 'partner'
    ? scoped.eq('partner_id', entityId as string)
    : scoped.or(`lead_id.eq.${entityId},client_id.eq.${entityId}`);

  return filtered.order('due_date', { ascending: false });
}

export function LeadDetailsPage() {
  return <UnifiedDetailsPage />;
}

/**
 * Task 5 "Partner drawer unificato": wrapper per la route `/partners/:id` (pagina intera,
 * fuori dal drawer di `PartnersPage`). `UnifiedDetailsPage` estrae `id` da `useParams` SOLO
 * per la modalità lead/client (`propLeadId || paramId`, vedi sotto) — la modalità partner
 * si attiva esclusivamente via prop esplicita `partnerId`, quindi qui il param va estratto
 * e passato a mano, stesso ruolo di `LeadDetailsPage` sopra ma per l'altra entità.
 */
export function PartnerDetailsRoute() {
  const { id } = useParams<{ id: string }>();
  return <UnifiedDetailsPage partnerId={id} />;
}

interface UnifiedDetailsPageProps {
  leadId?: string;      // ID passato come prop (alternativo a useParams)
  partnerId?: string;   // ID partner (mutuamente esclusivo con leadId): attiva la modalità entità 'partner'
  isModal?: boolean;    // Se true, nasconde header navigazione e adatta layout
  onClose?: () => void; // Callback per chiudere il modale
  pipelineContext?: string; // Pipeline da cui si sta visualizzando il lead (per filtrare note/attività)
  onActivityChanged?: () => void; // Notifica il parent che un'attività è stata modificata/eliminata
  isGlobalDrawer?: boolean; // true solo per il drawer globale del Layout (persona aperta sopra un'altra scheda)
}

export function UnifiedDetailsPage({ leadId: propLeadId, partnerId, isModal = false, onClose, pipelineContext, onActivityChanged, isGlobalDrawer = false }: UnifiedDetailsPageProps = {}) {
  const { id: paramId } = useParams<{ id: string }>();
  const id = propLeadId || paramId;
  const {user: fluUser} = useAuth();
  const {organizationId: fluOrganizationId} = useUserData();
  // Pipeline context: da prop (modale) o da query param (navigazione diretta)
  const [searchParams] = typeof window !== 'undefined' ? [new URLSearchParams(window.location.search)] : [new URLSearchParams()];
  const effectivePipelineContext = pipelineContext || searchParams.get('pipeline') || undefined;
  const navigate = useNavigate();
  const [routeSearchParams] = useSearchParams();
  const { selectedClient, loading, error, fetchClientById, convertLeadToClient, updateClient } = useClientStore();
  const { settings } = useSettingsStore();
  const { users, fetchUsers, userProfile } = useUserStore();
  const { notes, loading: notesLoading, fetchNotesByLeadId, createNote, updateNote, deleteNote } = useLeadNoteStore();
  const { createDeal } = useDealStore();
  const { leadStates, clientStates, fetchLeadStates, fetchClientStates } = useCustomStatesStore();
  const { getStagesForPipeline, fetchStagesByPipeline, stages: pipelineStagesMap } = useLeadPipelineStore();
  const { leadSources, fetchLeadSources } = useLeadSourceStore();
  // Fix review Task 3 "Partner drawer unificato": General Comments in modalità partner
  // deve scrivere su external_partners (vedi handleLeadUpdate sotto), non su clients.
  const { updatePartner } = usePartnerStore();
  const { toast, setDrawerOpen, confirm } = useApp();
  const { lead: leadLabel, client: clientLabel } = useMenuLabels();

  // === Task 2 "Partner drawer unificato": modalità partner ===
  // Attiva quando viene passato partnerId (mutuamente esclusivo con leadId).
  const isPartnerMode = !!partnerId;
  const {
    partner,
    partnerView,
    // Task 3 "Partner drawer unificato": campi extra (commissione, tariffa,
    // specializzazione, ecc.) per PartnerInfoExtras nei layout desktop/mobile.
    partnerExtras,
    partnerLoading,
    partnerError,
    fetchPartner,
    notes: partnerNotes,
    notesLoading: partnerNotesLoading,
    fetchNotes: fetchPartnerNotes,
    updateNote: updatePartnerNote,
    deleteNote: deletePartnerNote,
  } = usePartnerEntity(partnerId);

  // Hide tags for specific organization
  const shouldHideTags = userProfile?.organization_id === 'c47bcb07-dc78-49f0-b9b1-bb0b78a8d194';

  const isMounted = useRef(false);
  const initialClientLoadAttempted = useRef(false);

  // State variables
  const [isConverting, setIsConverting] = useState(false);
  const [showCreateDealModal, setShowCreateDealModal] = useState(false);
  useDrawerEscape(showCreateDealModal, () => { void confirm({ title: 'Chiudere l’offerta?', message: 'Le modifiche non salvate andranno perse.', confirmLabel:'Chiudi', cancelLabel:'Annulla', type:'warning' }).then(ok => { if(ok)setShowCreateDealModal(false); }); },65);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPartnerSelector, setShowPartnerSelector] = useState(false);
  // Task 5 "Partner drawer unificato": form di modifica del PARTNER (external_partners),
  // NON il PartnerSelector sopra (che assegna un partner referente a un lead/cliente —
  // concetto diverso). Apribile SOLO da "Modifica Partner" nell'header in modalità partner.
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [showExpandedDetails, setShowExpandedDetails] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [showStatusUpdateDrawer, setShowStatusUpdateDrawer] = useState(false);
  const [drawerDefaultTab, setDrawerDefaultTab] = useState<'note' | 'activity'>('note');
  const [editingActivity, setEditingActivity] = useState<any>(null);
  const [initialActivityData, setInitialActivityData] = useState<{ title: string; type: string; description: string; due_date?: string; due_time?: string } | null>(null);
  // Pre-fill dell'editor nel tab Nota: valorizzato SOLO dall'escalation del composer inline
  // ("Apri nel drawer completo"), azzerato in `onClose` come il pre-fill AI.
  const [initialNoteText, setInitialNoteText] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalTab, setNotesModalTab] = useState<'notes' | 'tasks'>('notes');
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showStatusEditor, setShowStatusEditor] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentClientId, setCurrentClientId] = useState<string | null>(null);

  // Convert custom lead states to lead stages for status management
  // If lead has a pipeline, use pipeline stages; otherwise use default lead_states
  const leadStagesFromDb: LeadStage[] = useMemo(() => {
    // Se il lead ha una pipeline personalizzata, usa le sue fasi
    if (selectedClient?.pipeline_id) {
      const pipelineStages = getStagesForPipeline(selectedClient.pipeline_id);
      return pipelineStages.map((stage, index) => ({
        id: stage.id,
        name: stage.name,
        color: stage.color,
        order: stage.sort_order || index + 1,
        is_won: stage.is_won,
        is_lost: stage.is_lost,
      }));
    }

    // Altrimenti usa le fasi di default (lead_states)
    return leadStates.map((state, index) => ({
      id: state.id,
      name: state.name,
      color: state.color,
      order: state.sort_order || index + 1,
      is_won: (state as any).is_won,
      is_lost: (state as any).is_lost,
    }));
    // pipelineStagesMap tra le dipendenze: senza, il memo restava vuoto quando le
    // fasi della pipeline arrivavano DOPO il primo render (navigazione diretta alla
    // scheda con cache fredda) e la riga Fase mostrava "Vuoto" per sempre.
  }, [selectedClient?.pipeline_id, leadStates, getStagesForPipeline, pipelineStagesMap]);

  // Opzioni "Stato" (client_states, distinto dalla Fase/pipeline): il colore viene
  // dallo stato, la description mostra "Lead"/"Cliente" nel dropdown cosi' si vede
  // subito a quale dei due appartiene ogni voce.
  const clientStateOptions = useMemo(
    () => clientStates
      .filter((s) => s.is_active !== false)
      .map((s) => ({ value: s.id, label: s.name, color: s.color, description: s.is_lead ? 'Lead' : 'Cliente' })),
    [clientStates],
  );

  // Transform users data for LeadStatusManager
  const availableUsers = users.map(user => ({
    id: user.id,
    email: user.email,
    full_name: user.full_name,
    profile_image_url: user.profile_image_url
  }));

  // Determine if this is a lead or client
  const isLead = selectedClient?.client_state?.is_lead;
  const entityType = isLead ? 'lead' : 'client';

  // === Task 2 "Partner drawer unificato": entità effettiva + view-model ===
  // entityKind è il tipo esteso ('partner' incluso) usato dal fetch/realtime/refetch;
  // non sostituisce `entityType` sopra (già non referenziato altrove nel file).
  const entityKind: 'lead' | 'client' | 'partner' = isPartnerMode ? 'partner' : (isLead ? 'lead' : 'client');
  // ID effettivo per fetch/realtime/refetch attività: partnerId in modalità partner,
  // altrimenti l'id lead/client di sempre.
  const effectiveEntityId = isPartnerMode ? partnerId : id;
  // View-model passato ai layout al posto di selectedClient: in modalità partner è il
  // mapping Client-like prodotto da usePartnerEntity; altrimenti (comportamento
  // invariato) selectedClient dal clientStore.
  const displayEntity = isPartnerMode ? partnerView : selectedClient;
  const displayNotes = isPartnerMode ? partnerNotes : notes;
  const displayNotesLoading = isPartnerMode ? partnerNotesLoading : notesLoading;

  // Set up translations - Force Italian
  const t = translations['it'].clients.details;
  const detailsT = {
    conversion: {
      button: `Converti in ${clientLabel.label}`,
      success: `${leadLabel.label} convertito con successo in ${clientLabel.labelLower}`,
      error: `Errore durante la conversione ${leadLabel.del}${leadLabel.labelLower}`
    },
    qualification: {
      title: `Qualificazione ${leadLabel.label}`,
      budget: 'Budget',
      timeline: 'Tempistiche',
      decisionMaker: 'Decisore',
      needs: 'Valutazione Esigenze',
      source: `Fonte ${leadLabel.label}`
    },
    details: {
      backToLeads: isLead ? `Torna ai ${leadLabel.plural}` : 'Torna alle Anagrafiche',
      entityInfo: isLead ? 'Informazioni' : 'Informazioni',
      expand: 'Espandi',
      collapse: 'Riduci',
      nameCompany: 'Nome/Azienda',
      email: 'Email',
      phone: 'Telefono',
      city: 'Città',
      clientCode: 'Codice Cliente',
      whatsapp: 'WhatsApp',
      address: 'Indirizzo',
      notFound: isLead ? `${leadLabel.label} non trovato` : `${clientLabel.label} non trovato`,
      notLead: `Questo non è un ${leadLabel.labelLower}. Utilizza la pagina dettagli ${clientLabel.labelLower}.`,
      goToClient: `Vai ai Dettagli ${clientLabel.label}`,
      converting: 'Conversione in corso...',
      unexpectedState: 'Stato imprevisto...'
    }
  };

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Set AI entity context for AI Help Chat
  // Skip in modalità partner: useAiHelpStore.setEntityContext supporta solo i tipi
  // 'lead' | 'client' (Task 2 non estende il tipo). In pratica l'effetto è già
  // inerte in modalità partner perché selectedClient (clientStore) resta null, ma
  // manteniamo la guardia esplicita per chiarezza e per sicurezza futura.
  useEffect(() => {
    const { setEntityContext } = useAiHelpStore.getState();
    if (!isPartnerMode && selectedClient?.id) {
      setEntityContext({
        type: selectedClient?.client_state?.is_lead ? 'lead' : 'client',
        id: selectedClient.id,
        summary: {
          name: selectedClient.business_name || `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim(),
          email: selectedClient.email,
          phone: selectedClient.phone,
          city: selectedClient.city,
          pipeline_stage: selectedClient.pipeline_stage?.name,
          lead_source: selectedClient.lead_source,
          entity_type: selectedClient.entity_type,
          assigned_to: (selectedClient as any).sales_rep?.full_name,
        },
      });
    }
    return () => setEntityContext(null);
  }, [selectedClient?.id, selectedClient?.pipeline_stage?.name, selectedClient?.client_state?.is_lead]);

  // Pausa il realtime service quando il modale di creazione offerta è aperto
  // Evita che i leads vengano modificati/rimossi mentre l'utente sta creando l'offerta
  useEffect(() => {
    if (showCreateDealModal) {
      leadsRealtimeService.pause();
    } else {
      leadsRealtimeService.resume();
    }
    return () => {
      // Assicura che il servizio sia ripreso quando il componente si smonta
      if (leadsRealtimeService.isPaused()) {
        leadsRealtimeService.resume();
      }
    };
  }, [showCreateDealModal]);

  // Unified data fetching
  useEffect(() => {
    // === Task 2 "Partner drawer unificato": ramo partner ===
    // Fetch dedicato: partner (usePartnerStore), note partner (usePartnerNoteStore),
    // attività (fetchEntityActivities con entityKind 'partner'). Niente
    // fetchLeadStates/fetchLeadSources/stages: non hanno senso per un partner.
    const fetchPartnerData = async () => {
      if (!partnerId) {
        initialClientLoadAttempted.current = false;
        setCurrentClientId(null);
        return;
      }

      if (currentClientId && currentClientId !== partnerId) {
        setIsInitialLoad(true);
      }

      setCurrentClientId(partnerId);
      initialClientLoadAttempted.current = true;
      setActivitiesLoading(true);

      try {
        const [partnerResult, , , activitiesResult] = await Promise.allSettled([
          fetchPartner(),
          fetchUsers(),
          fetchPartnerNotes(),
          fetchEntityActivities(partnerId, 'partner'),
        ]);

        if (partnerResult.status === 'rejected') {
          console.error('Error in fetchPartnerById call:', partnerResult.reason);
          toast.error('Errore nel caricamento dei dettagli del partner');
        } else {
          setIsInitialLoad(false);
        }

        if (activitiesResult.status === 'fulfilled' && !activitiesResult.value.error) {
          setActivities(activitiesResult.value.data || []);
        }
      } catch (error) {
        console.error('Error in partner data fetch:', error);
        toast.error('Errore nel caricamento dei dati');
      } finally {
        setActivitiesLoading(false);
      }
    };

    // Percorso lead/client — INVARIATO rispetto a prima della fattorizzazione:
    // stessa Promise.allSettled, stesso set di fetch, unica differenza è che la
    // query attività duplicata inline ora passa da fetchEntityActivities (stessa
    // select con embed users/assignee, stesso filtro .or(...), stesso order).
    const fetchAllData = async () => {
      if (!id) {
        initialClientLoadAttempted.current = false;
        setCurrentClientId(null);
        return;
      }

      if (currentClientId && currentClientId !== id) {
        setIsInitialLoad(true);
      }

      setCurrentClientId(id);
      initialClientLoadAttempted.current = true;
      setActivitiesLoading(true);

      try {
/*         console.log(`[LEAD_DETAILS_PAGE] 🚀 Starting data fetch for lead: ${id}`);
 */
        const [clientResult, usersResult, leadStatesResult, clientStatesResult, leadSourcesResult, notesResult, activitiesResult] = await Promise.allSettled([
          fetchClientById(id),
          fetchUsers(),
          fetchLeadStates(),
          fetchClientStates(),
          fetchLeadSources(),
          fetchNotesByLeadId(id, { pipelineId: effectivePipelineContext }),
          fetchEntityActivities(id, 'lead'),
        ]);

/*         console.log(`[LEAD_DETAILS_PAGE] 📊 Fetch results:`, {
          notesResult: notesResult.status,
          activitiesResult: activitiesResult.status,
          activitiesCount: activitiesResult.status === 'fulfilled' ? activitiesResult.value.data?.length : 0
        }); */

        if (clientResult.status === 'rejected') {
          console.error("Error in fetchClientById call:", clientResult.reason);
          toast.error(`Errore nel caricamento dei dettagli ${leadLabel.del}${leadLabel.labelLower}`);
        } else {
          setIsInitialLoad(false);
        }

        if (activitiesResult.status === 'fulfilled' && !activitiesResult.value.error) {
          setActivities(activitiesResult.value.data || []);
/*           console.log(`[LEAD_DETAILS_PAGE] ✅ Activities set:`, activitiesResult.value.data?.length);
 */        }

        // Log current notes from store
/*         console.log(`[LEAD_DETAILS_PAGE] 📝 Notes from store:`, {
          notesCount: notes.length,
          notes: notes
        }); */

      } catch (error) {
        console.error('Error in unified data fetch:', error);
        toast.error("Errore nel caricamento dei dati");
      } finally {
        setActivitiesLoading(false);
      }
    };

    if (isPartnerMode) {
      fetchPartnerData();
    } else {
      fetchAllData();
    }
    // Only depend on id/partnerId - store functions are stable references from Zustand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, partnerId, isPartnerMode]);

  // Il drawer globale del Layout usa questo stesso componente e lo stesso
  // selectedClient/note del clientStore: aprendo dalla scheda una persona
  // collegata, la persona prendeva il posto del cliente e, chiuso il drawer,
  // la scheda sotto restava sulla persona (le persone collegate "sparivano").
  // A drawer chiuso la pagina si riprende la propria anagrafica. Vale anche per le
  // schede aperte come drawer dalle pagine (es. lista Clienti): chiusa la persona,
  // sotto restava la persona e la X successiva chiudeva tutto (segnalato il 29/10/2026).
  // Escluso il drawer globale stesso: mentre si chiude ricaricherebbe la persona.
  const globalDrawerOpen = !!routeSearchParams.get('entityDrawer');
  useEffect(() => {
    if (isGlobalDrawer || isPartnerMode || !id || globalDrawerOpen) return;
    if (!selectedClient || selectedClient.id === id || useClientStore.getState().loading) return;
    fetchClientById(id);
    fetchNotesByLeadId(id, { pipelineId: effectivePipelineContext });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalDrawer, isPartnerMode, id, globalDrawerOpen, selectedClient?.id]);

  // Fetch pipeline stages when lead has a custom pipeline
  useEffect(() => {
    if (selectedClient?.pipeline_id && selectedClient.pipeline_id !== 'legacy') {
      fetchStagesByPipeline(selectedClient.pipeline_id);
    }
  }, [selectedClient?.pipeline_id, fetchStagesByPipeline]);

  // Realtime subscription for lead updates from workflows
  useEffect(() => {
    // Skip realtime in modal mode to avoid infinite loop with parent re-renders.
    // Skip anche in modalità partner: niente canale `clients` per un partner (Task 2).
    if (isModal || isPartnerMode) return;

    if (!id || !userProfile?.organization_id) return;

    const subscription = supabase
      .channel(`lead-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'clients',
          filter: `id=eq.${id}`
        },
        (payload) => {
          console.log('Lead updated via Realtime:', payload);

          // Skip refetch if status update drawer or form is open (user is editing)
          if (showStatusUpdateDrawer || showForm) {
            console.log('[LeadDetailsPage] Drawer or form open, skipping refetch');
            return;
          }

          fetchClientById(id, true);
          fetchNotesByLeadId(id, { pipelineId: effectivePipelineContext });
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userProfile?.organization_id]);

  // Realtime subscription for deal_notes changes
  useEffect(() => {
    // Skip realtime in modal mode to avoid infinite loop with parent re-renders.
    // Skip anche in modalità partner: canale dedicato con filtro partner_id più sotto (Task 2).
    if (isModal || isPartnerMode) return;

    if (!id || !userProfile?.organization_id) return;

    const subscription = supabase
      .channel(`lead-notes-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events: INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'deal_notes',
          filter: `lead_id=eq.${id}`
        },
        (payload) => {
          console.log('Lead notes updated via Realtime:', payload);
          fetchNotesByLeadId(id, { pipelineId: effectivePipelineContext });
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [id, userProfile?.organization_id, fetchNotesByLeadId, effectivePipelineContext]);

  // Realtime subscription for sales_activities changes (both lead_id and client_id)
  useEffect(() => {
    // Skip realtime in modal mode to avoid infinite loop with parent re-renders.
    // Skip anche in modalità partner: canale dedicato con filtro partner_id più sotto (Task 2).
    if (isModal || isPartnerMode) return;

    if (!id || !userProfile?.organization_id) return;

    const refreshActivities = async () => {
      const activitiesResult = await fetchEntityActivities(id, 'lead');
      if (activitiesResult.data) {
        setActivities(activitiesResult.data);
      }
    };

    const subscription = supabase
      .channel(`lead-activities-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sales_activities',
          filter: `lead_id=eq.${id}`
        },
        async (payload) => {
          console.log('Lead activities updated via Realtime (lead_id):', payload);
          await refreshActivities();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sales_activities',
          filter: `client_id=eq.${id}`
        },
        async (payload) => {
          console.log('Lead activities updated via Realtime (client_id):', payload);
          await refreshActivities();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [id, userProfile?.organization_id]);

  // === Task 2 "Partner drawer unificato": realtime modalità partner ===
  // Sostituisce, solo per partner e solo non-modal, i tre canali sopra (clients,
  // deal_notes lead_id, sales_activities lead_id/client_id) con due canali dedicati
  // filtrati su partner_id; niente canale `clients` (non applicabile a un partner).
  useEffect(() => {
    if (isModal || !isPartnerMode) return;
    if (!partnerId || !userProfile?.organization_id) return;

    const subscription = supabase
      .channel(`partner-notes-${partnerId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'deal_notes', filter: `partner_id=eq.${partnerId}` },
        () => { fetchPartnerNotes(); }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [partnerId, isPartnerMode, isModal, userProfile?.organization_id, fetchPartnerNotes]);

  useEffect(() => {
    if (isModal || !isPartnerMode) return;
    if (!partnerId || !userProfile?.organization_id) return;

    const refreshPartnerActivities = async () => {
      const activitiesResult = await fetchEntityActivities(partnerId, 'partner');
      if (activitiesResult.data) {
        setActivities(activitiesResult.data);
      }
    };

    const subscription = supabase
      .channel(`partner-activities-${partnerId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sales_activities', filter: `partner_id=eq.${partnerId}` },
        async () => { await refreshPartnerActivities(); }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [partnerId, isPartnerMode, isModal, userProfile?.organization_id]);

  // Handlers
  const handleConvertToClient = async () => {
    if (!selectedClient || !id) return;

    setIsConverting(true);
    try {
      await convertLeadToClient(id);
      toast.success(detailsT.conversion.success);
      if (!isModal) {
        navigate(`/clients/${id}`);
      } else if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error converting lead to client:", error);
      toast.error(detailsT.conversion.error);
    } finally {
      setIsConverting(false);
    }
  };

  const handleCreateDeal = () => {
    if (!selectedClient || !id) return;
    setShowCreateDealModal(true);
  };

  const handleEditLead = async () => {
    if (!selectedClient) return;
    console.log('[LeadDetailsPage handleEditLead] selectedClient passato al form:', {
      id: selectedClient.id,
      businessName: selectedClient.business_name,
      interests: selectedClient.interests,
      interest_categories: (selectedClient as any).interest_categories
    });
    setShowForm(true);
    // No need to fetch - selectedClient already has the data
  };

  /**
   * Task 5 "Partner drawer unificato": callback `onEdit` di LeadDetailsHeader/
   * LeadDesktopLayout (Task 4, già predisposta) per entityType === 'partner'. Apre
   * `PartnerForm` (lo stesso di PartnersPage/PartnerDetailsPage) invece di
   * ClientFormWizard — un partner non è una riga `clients`.
   */
  const handleEditPartner = () => {
    if (!partner) return;
    setShowPartnerForm(true);
  };

  const handleUpdate = async (formData: any) => {
    if (!selectedClient) return false;

    setIsSubmitting(true);
    try {
      console.log('[LeadDetailsPage] Updating lead with formData:', {
        leadId: selectedClient.id,
        pipeline_id: formData.pipeline_id,
        pipeline_stage_id: formData.pipeline_stage_id,
        fullFormData: formData
      });
      const result = await updateClient(selectedClient.id, formData);
      if (result) {
        setShowForm(false);
        // Gli interessi vengono sincronizzati automaticamente dallo store
        toast.success(`${leadLabel.label} aggiornato con successo`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error(`Errore durante l'aggiornamento ${leadLabel.del}${leadLabel.labelLower}`);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePartnerSelect = async (partner: any) => {
    if (!selectedClient) return;

    try {
      const partnerId = partner.id === null ? null : partner.id;
      await updateClient(selectedClient.id, { partner_id: partnerId });
      await fetchClientById(selectedClient.id, true);
      toast.success(partnerId ? 'Partner assegnato con successo' : 'Partner rimosso con successo');
    } catch (error) {
      console.error('Error updating partner:', error);
      toast.error('Errore durante l\'aggiornamento del partner');
    }
  };

  const handleDealCreated = async (dealData: any, services: any[], commissions?: any, referrerIds?: string[], contactIds?: string[]) => {
    try {
      const newDeal = await createDeal(dealData, services, contactIds);
      if (!newDeal) throw new Error('Creazione offerta non riuscita');
      try {
        if (referrerIds?.length) {
          const { useReferrerStore } = await import('../../lib/stores/referrerStore');
          for (const id of referrerIds) await useReferrerStore.getState().addDealReferrer(newDeal.id, id);
        }
        if (commissions && (commissions.user_commission_rate !== undefined || commissions.referrer_commission_overrides?.some((c: any) => c.commission_rate !== undefined || c.commission_type === 'fixed'))) {
          const { useDealCommissionStore } = await import('../../lib/stores/dealCommissionStore');
          await useDealCommissionStore.getState().createDealCommissionOverride(newDeal.id, commissions);
        }
      } catch (error) {
        console.error('Offerta creata, dati provvigioni incompleti:', error);
        toast.error('Offerta creata: verifica segnalatori e provvigioni nel dettaglio.');
      }

      setShowCreateDealModal(false);
      if (isModal && (useSettingsStore.getState().settings as any)?.open_created_deal_from_lead) {
        window.dispatchEvent(new CustomEvent('open-deal-drawer', { detail: { entityId: newDeal.id } }));
      }
      toast.success('Offerta creata con successo');
      if (!isModal) {
        navigate(`/deals/${newDeal.id}`);
      }
    } catch (error) {
      console.error('Error creating deal:', error);
      toast.error("Errore durante la creazione dell'offerta");
      throw error;
    }
  };

  const handleLeadUpdate = async (leadId: string, updates: any) => {
    try {
      // Fix review Task 3 "Partner drawer unificato": in modalità partner `leadId` è
      // in realtà l'id di `external_partners` (view-model `partnerView`), non un id
      // `clients`. Prima chiamava sempre `updateClient` → 'Client not found' silenzioso
      // (nessuna riga corrispondente) e il testo digitato in "Commenti Generali"
      // spariva senza feedback. L'unico caso raggiungibile qui in modalità partner è
      // il save di "Commenti Generali" (general_comments), che sui partner mappa su
      // `external_partners.notes` (vedi mapPartnerToClientView).
      if (isPartnerMode) {
        if (!partnerId || !('general_comments' in updates)) return false;
        const updated = await updatePartner(partnerId, { notes: updates.general_comments ?? '' });
        if (!updated) return false;
        // Rialinea il view-model (partnerView/partnerExtras) col nuovo valore, stesso
        // percorso usato da usePartnerEntity per il fetch iniziale.
        await fetchPartner();
        return true;
      }
      await updateClient(leadId, updates);
      await fetchClientById(leadId, true);
      return true;
    } catch (error) {
      console.error('Error updating lead:', error);
      return false;
    }
  };

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(() => {
      toast.success(`${label} copiato negli appunti`);
    });
  };

  const handleAddNote = () => {
    setDrawerDefaultTab('note');
    setShowStatusUpdateDrawer(true);
    setDrawerOpen(true);
  };

  const handleAddTask = (suggestedData?: { title: string; type: string; description: string; due_date?: string }) => {
    setDrawerDefaultTab('activity');
    setInitialNoteText(null);
    setInitialActivityData(suggestedData || null);
    setShowStatusUpdateDrawer(true);
    setDrawerOpen(true);
  };

  /** Escalation dal composer inline della cronologia: apre il drawer col contenuto gia' scritto. */
  const handleComposerOpenDrawer = (mode: ComposerMode, payload: ComposerDrawerPayload) => {
    if (mode === 'note') {
      setDrawerDefaultTab('note');
      setInitialActivityData(null);
      setInitialNoteText(payload.noteText || null);
    } else {
      setDrawerDefaultTab('activity');
      setInitialNoteText(null);
      setInitialActivityData({
        title: payload.title || '',
        type: payload.type || 'task',
        description: payload.description || '',
        due_date: payload.dueDate ? toComposerDateInputValue(payload.dueDate) : undefined,
        due_time: payload.dueDate ? toComposerTimeInputValue(payload.dueDate) : undefined,
      });
    }
    setShowStatusUpdateDrawer(true);
    setDrawerOpen(true);
  };

  const handleEditNote = async (noteId: string, content: string) => {
    try {
      // Task 2 "Partner drawer unificato": in modalità partner le note vivono nello
      // store dedicato (usePartnerNoteStore), non in useLeadNoteStore.
      if (isPartnerMode) {
        await updatePartnerNote(noteId, content);
      } else {
        await updateNote(noteId, content);
      }
      toast.success('Nota aggiornata con successo');
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Errore durante l\'aggiornamento della nota');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      if (isPartnerMode) {
        await deletePartnerNote(noteId);
      } else {
        await deleteNote(noteId);
      }
      toast.success('Nota eliminata con successo');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Errore durante l\'eliminazione della nota');
    }
  };

  const handleEditActivity = (activity: any) => {
    setEditingActivity(activity);
    setDrawerDefaultTab('activity');
    setShowStatusUpdateDrawer(true);
    setDrawerOpen(true);
  };

  const handleEditTask = async (taskId: string, updates: any) => {
    try {
      const { error } = await supabase
        .from('sales_activities')
        .update(updates)
        .eq('id', taskId);

      if (error) throw error;

      toast.success('Attività aggiornata con successo');
      // Refresh activities
      const activitiesResult = await fetchEntityActivities(effectiveEntityId, entityKind, false);

      if (activitiesResult.data) {
        setActivities(activitiesResult.data);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Errore durante l\'aggiornamento dell\'attività');
    }
  };

  const handleDeleteTask = async (taskId: string, opts?: { skipGoogleSync?: boolean }) => {
    try {
      // AlfaDocs si applica solo a lead/client (mai a un partner, Task 2).
      const activityToDelete = activities.find(a => a.id === taskId);
      let alfaApptId = (activityToDelete as any)?.metadata?.alfadocs_appointment_id;
      let alfaPracticeId: string | null = (activityToDelete as any)?.metadata?.alfadocs_practice_id || null;
      // Fallback: se metadata non è nello stato locale, leggi dal DB
      if (!isPartnerMode && !alfaApptId) {
        try {
          const { data: actFromDb } = await supabase
            .from('sales_activities')
            .select('metadata')
            .eq('id', taskId)
            .maybeSingle();
          alfaApptId = (actFromDb?.metadata as any)?.alfadocs_appointment_id;
          alfaPracticeId = alfaPracticeId || (actFromDb?.metadata as any)?.alfadocs_practice_id || null;
        } catch { /* colonna metadata potrebbe non esistere */ }
      }
      // Terzo fallback: cercare direttamente su AlfaDocs, su TUTTI gli studi collegati al cliente
      if (!isPartnerMode && !alfaApptId) {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const { data: profile } = await supabase.from('user_profiles').select('organization_id').eq('id', authUser?.id).single();
          if (profile?.organization_id && id) {
            const found = await findLinkedAlfaAppointment(id, activityToDelete as any, profile.organization_id);
            if (found) {
              alfaApptId = found.appointmentId;
              alfaPracticeId = found.practiceId;
            }
          }
        } catch (e) {
          console.warn('[handleDeleteTask] AlfaDocs appointment lookup failed:', e);
        }
      }

      // Bridge delete evento Google collegato: senza questo, cancellare l'attività
      // da qui lasciava l'evento orfano su Google (vedi calendarStore.deleteEvent,
      // unico altro punto che lo fa già). skipGoogleSync: il chiamante (modale
      // "Elimina con Google Calendar" in LeadNotesSection) ha già gestito la scelta
      // dell'utente sull'evento Google, non va rifatto qui.
      const activityRow = opts?.skipGoogleSync ? null : (await supabase
        .from('sales_activities')
        .select('google_event_id, calendar_id')
        .eq('id', taskId)
        .maybeSingle()).data;

      // Optimistic: remove immediately from UI before network call
      setActivities(prev => prev.filter(a => a.id !== taskId));
      const { error } = await supabase
        .from('sales_activities')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      if (activityRow?.google_event_id && activityRow?.calendar_id) {
        try {
          await useCalendarStore.getState().deleteGoogleEvent(activityRow.google_event_id, activityRow.calendar_id);
        } catch (gErr) {
          console.warn('[handleDeleteTask] Google delete bridge failed:', gErr);
        }
      }

      // Cancel on AlfaDocs if linked — instrada allo studio dell'APPUNTAMENTO (metadata o
      // practice trovata dal lookup), fallback primaria cliente, fallback connessione più vecchia.
      if (!isPartnerMode && alfaApptId) {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const { data: profile } = await supabase.from('user_profiles').select('organization_id').eq('id', authUser?.id).single();
          if (profile?.organization_id && id) {
            const configId = await resolveAlfaCancelConfigId(alfaPracticeId, id, profile.organization_id);
            await AlfaDocsService.updateAppointment(profile.organization_id, alfaApptId, { state: 'cancelled' }, { configId });
          }
        } catch (alfaErr) {
          console.warn('[LeadDetailsPage] AlfaDocs cancel failed:', alfaErr);
        }
      }

      toast.success('Attività eliminata con successo');
      onActivityChanged?.();
    } catch (error) {
      console.error('Error deleting task:', error);
      // Rollback: refetch to restore correct state
      const { data: freshActivities } = await fetchEntityActivities(effectiveEntityId, entityKind);
      if (freshActivities) setActivities(freshActivities);
      toast.error('Errore durante l\'eliminazione dell\'attività');
    }
  };

  const handleDeleteActivity = async (activityId: string, opts?: { skipGoogleSync?: boolean }) => {
    try {
      // AlfaDocs si applica solo a lead/client (mai a un partner, Task 2).
      const activityToDelete = activities.find(a => a.id === activityId);
      let alfaApptId = (activityToDelete as any)?.metadata?.alfadocs_appointment_id;
      let alfaPracticeId: string | null = (activityToDelete as any)?.metadata?.alfadocs_practice_id || null;
      // Fallback: se metadata non è nello stato locale, leggi dal DB
      if (!isPartnerMode && !alfaApptId) {
        try {
          const { data: actFromDb } = await supabase
            .from('sales_activities')
            .select('metadata')
            .eq('id', activityId)
            .maybeSingle();
          alfaApptId = (actFromDb?.metadata as any)?.alfadocs_appointment_id;
          alfaPracticeId = alfaPracticeId || (actFromDb?.metadata as any)?.alfadocs_practice_id || null;
        } catch { /* colonna metadata potrebbe non esistere */ }
      }
      // Terzo fallback: cercare direttamente su AlfaDocs, su TUTTI gli studi collegati al cliente
      if (!isPartnerMode && !alfaApptId) {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const { data: profile } = await supabase.from('user_profiles').select('organization_id').eq('id', authUser?.id).single();
          if (profile?.organization_id && id) {
            const found = await findLinkedAlfaAppointment(id, activityToDelete as any, profile.organization_id);
            if (found) {
              alfaApptId = found.appointmentId;
              alfaPracticeId = found.practiceId;
            }
          }
        } catch (e) {
          console.warn('[handleDeleteActivity] AlfaDocs appointment lookup failed:', e);
        }
      }
      // Bridge delete evento Google collegato (vedi handleDeleteTask sopra e
      // calendarStore.deleteEvent per lo stesso pattern). skipGoogleSync: il
      // modale "Elimina con Google Calendar" ha già gestito la scelta dell'utente.
      const activityRow = opts?.skipGoogleSync ? null : (await supabase
        .from('sales_activities')
        .select('google_event_id, calendar_id')
        .eq('id', activityId)
        .maybeSingle()).data;

      const { error } = await supabase
        .from('sales_activities')
        .delete()
        .eq('id', activityId);

      if (error) throw error;

      if (activityRow?.google_event_id && activityRow?.calendar_id) {
        try {
          await useCalendarStore.getState().deleteGoogleEvent(activityRow.google_event_id, activityRow.calendar_id);
        } catch (gErr) {
          console.warn('[handleDeleteActivity] Google delete bridge failed:', gErr);
        }
      }

      // Cancel on AlfaDocs if linked — instrada allo studio dell'APPUNTAMENTO (metadata o
      // practice trovata dal lookup), fallback primaria cliente, fallback connessione più vecchia.
      if (!isPartnerMode && alfaApptId) {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const { data: profile } = await supabase.from('user_profiles').select('organization_id').eq('id', authUser?.id).single();
          if (profile?.organization_id && id) {
            const configId = await resolveAlfaCancelConfigId(alfaPracticeId, id, profile.organization_id);
            await AlfaDocsService.updateAppointment(profile.organization_id, alfaApptId, { state: 'cancelled' }, { configId });
          }
        } catch (alfaErr) {
          console.warn('[LeadDetailsPage] AlfaDocs cancel failed:', alfaErr);
        }
      }

      toast.success('Attività eliminata con successo');
      onActivityChanged?.();
      // Remove from local state immediately for responsive UI
      setActivities(prev => prev.filter(a => a.id !== activityId));
      // Refetch from DB to ensure consistency (realtime may not trigger)
      const { data: freshActivities } = await fetchEntityActivities(effectiveEntityId, entityKind);
      if (freshActivities) {
        setActivities(freshActivities);
      }
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Errore durante l\'eliminazione dell\'attività');
    }
  };

  const handleUpdateActivities = (updatedActivities: any[]) => {
    setActivities(updatedActivities);
    onActivityChanged?.();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Memoized drawer callbacks to prevent unnecessary re-renders
  const handleStageUpdated = useCallback(() => {
    if (selectedClient?.id) {
      fetchClientById(selectedClient.id, true);
    }
  }, [selectedClient?.id, fetchClientById]);

  const handleNotesUpdated = useCallback(() => {
    // Task 2 "Partner drawer unificato": in modalità partner le note si ricaricano
    // da usePartnerNoteStore (deal_notes.partner_id), non da fetchNotesByLeadId.
    if (isPartnerMode) {
      fetchPartnerNotes();
    } else if (id) {
      fetchNotesByLeadId(id, { pipelineId: effectivePipelineContext });
    }
  }, [id, isPartnerMode, fetchNotesByLeadId, fetchPartnerNotes, effectivePipelineContext]);

  /**
   * Ricarica le attivita' della scheda: stessa query gia' usata dalle callback
   * `onActivityAdded`/`onActivityUpdated` del drawer.
   */
  const refreshActivities = useCallback(async () => {
    if (!effectiveEntityId) return;
    const activitiesResult = await fetchEntityActivities(effectiveEntityId, entityKind, false);
    if (activitiesResult.data) setActivities(activitiesResult.data);
  }, [effectiveEntityId, entityKind]);

  // Modal details intentionally skip realtime subscriptions. A verified Flù
  // receipt also refreshes that already-mounted view, without replaying a write.
  useEffect(() => {
    const organizationId = fluOrganizationId;
    const userId = fluUser?.id;
    if (isPartnerMode || !effectiveEntityId || !organizationId || !userId) return;
    return watchFluActivities(window, organizationId + ':' + userId, effectiveEntityId, async isCurrent => {
      const result = await fetchEntityActivities(effectiveEntityId, entityKind, true, organizationId);
      if (!isCurrent()) return;
      if (result.error) throw result.error;
      if (result.data) setActivities(result.data);
    }, () => toast.error('La modifica è salvata, ma non riesco ad aggiornare le attività. Riapri la scheda per rileggerle.'));
  }, [effectiveEntityId, entityKind, isPartnerMode, fluOrganizationId, fluUser?.id]);

  useEffect(() => {
    if (isPartnerMode || !effectiveEntityId || !fluOrganizationId || !fluUser?.id) return;
    return watchFluNotes(window, fluOrganizationId + ':' + fluUser.id, effectiveEntityId, async isCurrent => {
      if (!isCurrent()) return;
      await fetchNotesByLeadId(effectiveEntityId, {pipelineId: effectivePipelineContext, organizationId: fluOrganizationId, isCurrent});
    });
  }, [effectiveEntityId, effectivePipelineContext, isPartnerMode, fluOrganizationId, fluUser?.id, fetchNotesByLeadId]);

  useEffect(() => {
    if (isPartnerMode || !effectiveEntityId || !fluOrganizationId || !fluUser?.id) return;
    return watchFluClient(window, fluOrganizationId + ':' + fluUser.id, effectiveEntityId, async isCurrent => {
      const result = await supabase.from('clients').select('id,business_name,email,phone,lead_source,lead_source_details,pipeline_id,pipeline_stage_id').eq('organization_id', fluOrganizationId).eq('id', effectiveEntityId).maybeSingle();
      if (!isCurrent()) return;
      if (result.error || !result.data) throw Error('client_refresh_unavailable');
      if (hasOpenCrmForm(document)) throw Error('client_form_open');
      const fresh = result.data;
      useClientStore.setState(current => current.selectedClient?.id === fresh.id ? {selectedClient: {...current.selectedClient, ...fresh}} : {});
    }, () => toast.error('La modifica è salvata. Chiudi eventuali moduli e riapri la scheda per rileggerla.'));
  }, [effectiveEntityId, isPartnerMode, fluOrganizationId, fluUser?.id]);

  /** Refresh dopo un salvataggio dal composer inline (note + attivita'). */
  const handleComposerSaved = useCallback(async () => {
    handleNotesUpdated();
    await refreshActivities();
    onActivityChanged?.();
  }, [handleNotesUpdated, refreshActivities, onActivityChanged]);

  const composerWiring = useMemo(
    () => ({ onSaved: handleComposerSaved, onOpenDrawer: handleComposerOpenDrawer }),
    [handleComposerSaved] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleAssigneeUpdated = useCallback(() => {
    if (selectedClient?.id) {
      fetchClientById(selectedClient.id, true);
    }
  }, [selectedClient?.id, fetchClientById]);

  // Loading states
  // Task 2 "Partner drawer unificato": in modalità partner questi controlli usano
  // partnerLoading/partnerError/partner (usePartnerEntity) invece di
  // loading/error/selectedClient (clientStore, che in modalità partner resta
  // invariato/stale — non deve pilotare la UI).
  if (isPartnerMode) {
    if (partnerLoading && !initialClientLoadAttempted.current) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      );
    }

    if (partnerError) {
      return <div className="text-red-400 p-4">Error: {partnerError}</div>;
    }

    if (!partner && initialClientLoadAttempted.current && !partnerLoading) {
      return <div className="p-4 text-center text-black/60">Partner non trovato. ID: {partnerId}</div>;
    }
  } else {
    if (loading && !initialClientLoadAttempted.current) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      );
    }

    if (error) {
      return <div className="text-red-400 p-4">Error: {error}</div>;
    }

    if (!selectedClient && initialClientLoadAttempted.current && !loading) {
      return <div className="p-4 text-center text-black/60">{detailsT.details.notFound} ID: {id}</div>;
    }
  }

  const showSkeleton = isPartnerMode
    ? isInitialLoad && (!partner || partner.id !== partnerId)
    : isInitialLoad && (!selectedClient || selectedClient.id !== id);

  if (isPartnerMode) {
    if (!partner && !showSkeleton) {
      return (
        <div className="flex justify-center items-center h-64">
          <p className="text-black/50 mr-4">{detailsT.details.unexpectedState}</p>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-black"></div>
        </div>
      );
    }
  } else {
    if (!selectedClient && !showSkeleton) {
      return (
        <div className="flex justify-center items-center h-64">
          <p className="text-black/50 mr-4">{detailsT.details.unexpectedState}</p>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-black"></div>
        </div>
      );
    }
  }

  // Show skeleton layout while loading
  if (showSkeleton) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 overflow-x-hidden">
        <HeaderSkeleton />
        <div className="lg:grid lg:grid-cols-3 lg:gap-4">
          <div className="lg:hidden space-y-3">
            <CardSkeleton title={false} lines={4} />
            <CardSkeleton lines={3} />
            <TagsSkeleton />
            <ContactsSkeleton />
            <InterestsSkeleton />
            <CustomFieldsSkeleton />
            <NotesSkeleton />
          </div>
          <div className="hidden lg:block lg:col-span-2 space-y-4">
            <CardSkeleton lines={6} />
            <div className="grid grid-cols-2 gap-4">
              <CardSkeleton lines={4} />
              <CardSkeleton lines={4} />
            </div>
            <TagsSkeleton />
            <ContactsSkeleton />
            <InterestsSkeleton />
            <CustomFieldsSkeleton />
          </div>
          <div className="hidden lg:block space-y-4">
            <StatusManagerSkeleton />
            <CardSkeleton lines={2} />
            <NotesSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={isModal ? "flex flex-col h-full" : "max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 overflow-x-hidden"}>
        {/* Header - only when NOT in modal mode */}
        {!isModal && (
          <LeadDetailsHeader
            selectedClient={displayEntity}
            entityType={entityKind}
            isLead={isLead}
            showActionsMenu={showActionsMenu}
            setShowActionsMenu={setShowActionsMenu}
            isConverting={isConverting}
            onEditLead={handleEditLead}
            // Task 5 "Partner drawer unificato": apre PartnerForm invece di ClientFormWizard
            // quando entityType === 'partner' (guardia già dentro LeadDetailsHeader, Task 4).
            onEdit={handleEditPartner}
            onCreateDeal={() => setShowCreateDealModal(true)}
            onConvertToClient={handleConvertToClient}
            detailsT={detailsT}
            isModal={isModal}
            onClose={onClose}
          />
        )}

        <div className={isModal ? "flex-1 h-full" : ""}>
              <LeadMobileLayout
                selectedClient={displayEntity}
                entityType={entityKind}
                isLead={isLead}
                showStatusEditor={showStatusEditor}
                setShowStatusEditor={setShowStatusEditor}
                showExpandedDetails={showExpandedDetails}
                setShowExpandedDetails={setShowExpandedDetails}
                shouldHideTags={shouldHideTags}
                leadStagesFromDb={leadStagesFromDb}
                clientStateOptions={clientStateOptions}
                availableUsers={availableUsers}
                leadSources={leadSources}
                notes={displayNotes}
                activities={activities}
                notesLoading={displayNotesLoading}
                t={t}
                formatDate={formatDate}
                handleLeadUpdate={handleLeadUpdate}
                handleAddNote={handleAddNote}
                handleAddTask={handleAddTask}
                handleEditNote={handleEditNote}
                handleDeleteNote={handleDeleteNote}
                handleDeleteTask={handleDeleteTask}
                handleEditTask={handleEditActivity}
                handleUpdateActivities={handleUpdateActivities}
                composer={composerWiring}
                showSkeleton={showSkeleton}
                // Task 3 "Partner drawer unificato": entità partner grezza + campi extra
                // (undefined/null fuori da isPartnerMode, nessun impatto su lead/client).
                partner={partner}
                partnerExtras={partnerExtras}
                // Props per modale
                isModal={isModal}
                onClose={onClose}
                onEditLead={handleEditLead}
                // Task 5: gemello mobile di onEdit (già presente su desktop, Task 4).
                onEdit={handleEditPartner}
                onCreateDeal={() => setShowCreateDealModal(true)}
                onConvertToClient={handleConvertToClient}
                isConverting={isConverting}
                detailsT={detailsT}
              />

              <LeadDesktopLayout
                selectedClient={displayEntity}
                entityType={entityKind}
                isLead={isLead}
                showExpandedDetails={showExpandedDetails}
                setShowExpandedDetails={setShowExpandedDetails}
                shouldHideTags={shouldHideTags}
                leadStagesFromDb={leadStagesFromDb}
                clientStateOptions={clientStateOptions}
                availableUsers={availableUsers}
                notes={displayNotes}
                activities={activities}
                notesLoading={displayNotesLoading}
                t={t}
                detailsT={detailsT}
                formatDate={formatDate}
                handleLeadUpdate={handleLeadUpdate}
                handleEditLead={handleEditLead}
                // Task 5: consumata da LeadDetailsHeader in modalità partner (Task 4 già
                // predisponeva la prop `onEdit` su questo componente).
                onEdit={handleEditPartner}
                handleCopy={handleCopy}
                handleAddNote={handleAddNote}
                handleAddTask={handleAddTask}
                handleEditNote={handleEditNote}
                handleDeleteNote={handleDeleteNote}
                handleDeleteTask={handleDeleteTask}
                handleEditTask={handleEditActivity}
                handleUpdateActivities={handleUpdateActivities}
                composer={composerWiring}
                onAddPartner={() => setShowPartnerSelector(true)}
                showSkeleton={showSkeleton}
                // Task 3 "Partner drawer unificato": entità partner grezza + campi extra
                // (undefined/null fuori da isPartnerMode, nessun impatto su lead/client).
                partner={partner}
                partnerExtras={partnerExtras}
                isModal={isModal}
                // Header props for modal mode
                showActionsMenu={showActionsMenu}
                setShowActionsMenu={setShowActionsMenu}
                isConverting={isConverting}
                onCreateDeal={() => setShowCreateDealModal(true)}
                onConvertToClient={handleConvertToClient}
                onClose={onClose}
                pipelineContext={effectivePipelineContext}
              />
        </div>

        {/* Modifica lead/cliente: drawer anagrafica in modalità edit (sostituisce
            il wizard modale dal redesign pannello 14/08; il wizard resta sui punti
            di modifica non ancora migrati) */}
        {selectedClient && (
          <CreateClientDrawer
            isOpen={showForm && !!selectedClient}
            mode="edit"
            initialData={clientToDrawerInitialData(selectedClient)}
            isLead={!!isLead}
            isSubmitting={isSubmitting}
            onComplete={handleUpdate}
            onClose={() => setShowForm(false)}
          />
        )}

        {/* Task 5 "Partner drawer unificato": drawer di modifica PARTNER — stesso PartnerForm
            già usato da PartnersPage/PartnerDetailsPage, aperto SOLO da "Modifica Partner"
            nell'header (handleEditPartner), mai per lead/client (showPartnerForm resta
            sempre false in quei rami: nessun bottone lo imposta a true).
            Drawer e non modal (regola UI): il corpo scorre, il form ha la sua intestazione. */}
        <NotionDrawer
          isOpen={showPartnerForm && !!partner}
          onClose={() => setShowPartnerForm(false)}
          width="3xl"
          noBodyPadding
        >
          {partner && (
            <PartnerForm
              partner={partner}
              onClose={() => setShowPartnerForm(false)}
              onSuccess={async () => {
                setShowPartnerForm(false);
                await fetchPartner();
              }}
            />
          )}
        </NotionDrawer>

        {/* Task 5 "Partner drawer unificato": onClose e' invariata per lead/client (il ramo
            `if (id)` resta condizionato allo stesso `id`, sempre assente in modalita'
            partner) — condivisa dai DUE drawer sotto per non tenere due chiusure allineate
            a mano. onActivityAdded/onActivityUpdated erano gia' scritte in termini di
            effectiveEntityId/entityKind (partner-safe dal Task 2), quindi si riusano tali e
            quali. */}
        {(() => {
          const handleStatusDrawerClose = () => {
            setShowStatusUpdateDrawer(false);
            // Refetch data when drawer closes to sync any external changes.
            // Fix-wave "Partner drawer unificato" (finding MINOR): sulla route intera
            // /partners/:id `id` è l'id del partner, non un id `clients` — senza il
            // guard queste due fetch venivano lanciate a vuoto contro clientStore
            // (404/errore silenzioso, stato di errore inutile nello store).
            if (id && !isPartnerMode) {
              fetchClientById(id, true);
              fetchNotesByLeadId(id, { pipelineId: effectivePipelineContext });
              // Clear buffered events since we just refetched fresh data
              leadsRealtimeService.clearBuffer();
            }
            setDrawerOpen(false);
            setEditingActivity(null);
            setInitialActivityData(null);
            setInitialNoteText(null);
          };
          const handleActivityAdded = () => {
            // Refresh activities after adding
            const refreshActivities = async () => {
              const activitiesResult = await fetchEntityActivities(effectiveEntityId, entityKind, false);
              if (activitiesResult.data) {
                setActivities(activitiesResult.data);
              }
            };
            refreshActivities();
          };
          const handleActivityUpdated = () => {
            // Refresh activities after updating
            const refreshActivities = async () => {
              const activitiesResult = await fetchEntityActivities(effectiveEntityId, entityKind, false);
              if (activitiesResult.data) {
                setActivities(activitiesResult.data);
              }
            };
            refreshActivities();
            setEditingActivity(null);
          };

          return (
            <>
              <LeadStatusUpdateDrawer
                isOpen={showStatusUpdateDrawer && !isPartnerMode}
                onClose={handleStatusDrawerClose}
                currentStage={selectedClient?.pipeline_id ? selectedClient?.pipeline_stage_id : selectedClient?.lead_stage || 'new'}
                leadId={id || ''}
                currentAssignee={selectedClient?.assigned_to}
                currentSalesRepId={selectedClient?.sales_rep_id}
                leadStages={leadStagesFromDb}
                leadPipelineId={selectedClient?.pipeline_id || null}
                onStageUpdated={handleStageUpdated}
                onNotesUpdated={handleNotesUpdated}
                onAssigneeUpdated={handleAssigneeUpdated}
                onActivityAdded={handleActivityAdded}
                translations={t}
                defaultTab={drawerDefaultTab}
                // Chi fissa un'attivita' di solito deve anche far avanzare il lead
                // (CSE 11/09/2026: i lead restavano in "Nuovo Lead" dopo l'appuntamento
                // perche' lo slot "Fase lead" era chiuso e nessuno lo apriva).
                defaultStatusOpen={drawerDefaultTab === 'activity'}
                editingActivity={editingActivity}
                initialActivityData={initialActivityData}
                initialNoteText={initialNoteText}
                pipelineContext={effectivePipelineContext}
                onActivityUpdated={handleActivityUpdated}
              />

              {/* Task 5 "Partner drawer unificato": stesso drawer condiviso Nota/Attività, ma
                  con l'adapter 'partner' (linkageFields → partner_id, MAI lead_id) e senza
                  slot "Fase lead" (non applicabile: la fase pipeline partner si gestisce nel
                  PartnerStageSlot, Task 4). Monta SOLO in modalita' partner, cosi' i pulsanti
                  "Nota"/"Attività" dell'header di LeadNotesSection e l'escalation del
                  composer inline ("Apri nel drawer completo") funzionano davvero anche per un
                  partner, invece di essere bloccati in silenzio da leadId vuoto. */}
              {isPartnerMode && (
                <PartnerStatusUpdateDrawer
                  isOpen={showStatusUpdateDrawer && isPartnerMode}
                  onClose={handleStatusDrawerClose}
                  partnerId={partnerId || ''}
                  onNotesUpdated={handleNotesUpdated}
                  onActivityAdded={handleActivityAdded}
                  onActivityUpdated={handleActivityUpdated}
                  translations={t}
                  defaultTab={drawerDefaultTab}
                  editingActivity={editingActivity}
                  initialActivityData={initialActivityData}
                  initialNoteText={initialNoteText}
                  pipelineContext={effectivePipelineContext}
                />
              )}
            </>
          );
        })()}

        {showCreateDealModal && selectedClient && (
          <>
            <div
              className="fixed inset-0 z-[65] bg-black/10 animate-[fadeIn_0.3s_ease-out] md:top-12 max-md:!left-0"
              style={{ left: 'var(--sidebar-width, 0px)' }}
              onClick={async () => {
                const confirmed = await confirm({
                  title: 'Chiudere l\'offerta?',
                  message: 'Sei sicuro di voler chiudere? Le modifiche non salvate andranno perse.',
                  confirmLabel: 'Chiudi offerta',
                  cancelLabel: 'Annulla',
                  type: 'danger'
                });
                if (confirmed) setShowCreateDealModal(false);
              }}
            />
            <div
              className="fixed top-0 right-0 bottom-0 z-[65] bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto md:top-12 max-md:!left-0 animate-[slideInRight_0.25s_ease-out]"
              style={{ left: 'var(--sidebar-width, 0px)' }}
            >
              <button
                onClick={async () => {
                  const confirmed = await confirm({
                    title: 'Chiudere l\'offerta?',
                    message: 'Sei sicuro di voler chiudere? Le modifiche non salvate andranno perse.',
                    confirmLabel: 'Chiudi',
                    cancelLabel: 'Annulla',
                    type: 'warning'
                  });
                  if (confirmed) setShowCreateDealModal(false);
                }}
                className="absolute top-3 left-2 z-50 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-black/5 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-white/5 transition-colors"
                title="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
              <DealForm
                clientId={selectedClient.id}
                leadId={selectedClient.id}
                onSubmit={handleDealCreated}
                onCancel={async () => {
                  const confirmed = await confirm({
                    title: 'Chiudere l\'offerta?',
                    message: 'Sei sicuro di voler chiudere? Le modifiche non salvate andranno perse.',
                    confirmLabel: 'Chiudi',
                    cancelLabel: 'Annulla',
                    type: 'warning'
                  });
                  if (confirmed) setShowCreateDealModal(false);
                }}
              />
            </div>
          </>
        )}
      </div>

      <LeadNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        notesModalTab={notesModalTab}
        setNotesModalTab={setNotesModalTab}
        selectedClient={displayEntity}
        notes={displayNotes}
        activities={activities}
        notesLoading={displayNotesLoading}
        translations={t}
        formatDate={formatDate}
        onAddNote={handleAddNote}
        onAddTask={handleAddTask}
        onEditNote={handleEditNote}
        onDeleteNote={handleDeleteNote}
        onDeleteActivity={handleDeleteActivity}
        onUpdateActivities={handleUpdateActivities}
        toast={toast}
        showSkeleton={showSkeleton}
      />

      <PartnerSelector
        isOpen={showPartnerSelector}
        onClose={() => setShowPartnerSelector(false)}
        onSelectPartner={handlePartnerSelect}
        selectedPartnerId={selectedClient?.partner_id}
      />
    </>
  );
}